import Vue from "vue";

/**
 * Handle a vote request.
 * If the vote is from a seat that is already locked, ignore it.
 * @param state session state
 * @param index seat of the player in the circle
 * @param vote true or false
 */
const handleVote = (state, [index, vote]) => {
  if (!state.nomination) return;
  state.votes = [...state.votes];
  state.votes[index] = vote === undefined ? !state.votes[index] : vote;
};

const state = () => ({
  sessionId: "",
  isSpectator: false,
  isReconnecting: false,
  playerCount: 0,
  ping: 0,
  playerId: "",
  claimedSeat: -1,
  nomination: false,
  votes: [],
  lockedVote: 0,
  votingSpeed: 500,
  isVoteInProgress: false,
  voteHistory: [],
  markedPlayer: -1,
  isVoteHistoryAllowed: true,
  isRolesDistributed: false,
  bluffsRecipientId: "", // ID of player who just received bluffs (for highlight animation)
  chats: {}, // playerId -> { messages: [], unreadCount: 0 }
  currentChatPlayerId: null,
  isChatMinimized: false,
  pendingAction: null, // Action to execute after nickname is set
  screenshotViewer: null, // { msgId, imageData, mode, ttl, viewedAt } when viewer is open

});

const getters = {};

const actions = {};

// mutations helper functions
const set = (key) => (state, val) => {
  state[key] = val;
};

const mutations = {
  setPlayerId: set("playerId"),
  setSpectator: set("isSpectator"),
  setReconnecting: set("isReconnecting"),
  setPlayerCount: set("playerCount"),
  setPing: set("ping"),
  setVotingSpeed: set("votingSpeed"),
  setVoteInProgress: set("isVoteInProgress"),
  setMarkedPlayer: set("markedPlayer"),
  setNomination: set("nomination"),
  setVoteHistoryAllowed: set("isVoteHistoryAllowed"),
  claimSeat: set("claimedSeat"),
  distributeRoles: set("isRolesDistributed"),
  setPendingAction: set("pendingAction"),
  setSessionId(state, sessionId) {
    state.sessionId = sessionId
      .toLocaleLowerCase()
      .replace(/[^0-9a-z]/g, "")
      .substr(0, 10);
  },
  nomination(
    state,
    { nomination, votes, votingSpeed, lockedVote, isVoteInProgress } = {},
  ) {
    state.nomination = nomination || false;
    state.votes = votes || [];
    state.votingSpeed = votingSpeed || state.votingSpeed;
    state.lockedVote = lockedVote || 0;
    state.isVoteInProgress = isVoteInProgress || false;
  },
  /**
   * Create an entry in the vote history log. Requires current player array because it might change later in the game.
   * Only stores votes that were completed.
   * @param state
   * @param players
   */
  addHistory(state, players) {
    if (!state.isVoteHistoryAllowed && state.isSpectator) return;
    if (!state.nomination || state.lockedVote <= players.length) return;
    const isExile = players[state.nomination[1]].role.team === "traveler";
    state.voteHistory.push({
      timestamp: new Date(),
      nominatorIndex: state.nomination[0],
      nominator: players[state.nomination[0]].name,
      nomineeIndex: state.nomination[1],
      nominee: players[state.nomination[1]].name,
      type: isExile ? "Exile" : "Execution",
      majority: Math.ceil(
        players.filter((player) => !player.isDead || isExile).length / 2,
      ),
      votes: players
        .filter((player, index) => state.votes[index])
        .map((player) => ({
          index: players.indexOf(player),
          name: player.name,
        })),
    });
  },
  clearVoteHistory(state) {
    state.voteHistory = [];
  },
  /**
   * Store a vote with and without syncing it to the live session.
   * This is necessary in order to prevent infinite voting loops.
   * @param state
   * @param vote
   */
  vote: handleVote,
  voteSync: handleVote,
  lockVote(state, lock) {
    state.lockedVote = lock !== undefined ? lock : state.lockedVote + 1;
  },
  setCurrentChat: set("currentChatPlayerId"),
  setChatMinimized: set("isChatMinimized"),
  initChat(state, playerId) {
    if (!state.chats[playerId]) {
      Vue.set(state.chats, playerId, {
        messages: [],
        unreadCount: 0,
      });
    }
  },
  sendChatMessage(state, { targetPlayerId, content }) {
    if (!state.chats[targetPlayerId]) {
      Vue.set(state.chats, targetPlayerId, {
        messages: [],
        unreadCount: 0,
      });
    }
    state.chats[targetPlayerId].messages.push({
      senderId: state.playerId,
      senderName: state.isSpectator ? "You" : "Storyteller",
      content,
      timestamp: Date.now(),
    });
  },
  receiveChatMessage(state, { fromPlayerId, senderName, content, timestamp }) {
    if (!state.chats[fromPlayerId]) {
      Vue.set(state.chats, fromPlayerId, {
        messages: [],
        unreadCount: 0,
      });
    }
    
    // Check for duplicate message (same timestamp and content)
    const isDuplicate = state.chats[fromPlayerId].messages.some(
      msg => msg.timestamp === timestamp && msg.content === content
    );
    
    if (isDuplicate) {
      console.log("[Chat] Duplicate message ignored:", { fromPlayerId, timestamp, content });
      return;
    }
    
    state.chats[fromPlayerId].messages.push({
      senderId: fromPlayerId,
      senderName,
      content,
      timestamp,
    });
    
    // Sort messages by timestamp to maintain order
    state.chats[fromPlayerId].messages.sort((a, b) => a.timestamp - b.timestamp);
    
    // Increment unread count if chat is not currently open OR if chat is minimized
    if (state.currentChatPlayerId !== fromPlayerId || state.isChatMinimized) {
      state.chats[fromPlayerId].unreadCount++;
    }
  },
  markChatAsRead(state, playerId) {
    if (state.chats[playerId]) {
      state.chats[playerId].unreadCount = 0;
    }
  },
  updateNickname() {
    // This mutation is a trigger for socket.js to broadcast the nickname update
    // The actual nickname is already stored in grimoire.nickname
  },
  sendBluffsToPlayer(state, playerId) {
    // This mutation is a trigger for socket.js to send bluffs to a specific player
    // The actual bluffs data will be gathered from the players store
    // Set the recipient ID to trigger the highlight animation
    state.bluffsRecipientId = playerId;
    
    // Auto-clear the recipient ID after animation completes (1 second)
    setTimeout(() => {
      state.bluffsRecipientId = "";
    }, 1000);
  },
  updateChatSenderName(state, { playerId, nickname }) {
    // Update sender names in all chat histories
    Object.keys(state.chats).forEach((chatPlayerId) => {
      const chat = state.chats[chatPlayerId];
      if (chat && chat.messages) {
        chat.messages.forEach((message) => {
          if (message.senderId === playerId) {
            // Update the sender name based on whether it's storyteller or a player
            if (playerId === "host" || playerId === "storyteller") {
              message.senderName = "Storyteller";
            } else {
              // For players, we need to find their seat number
              // This will be updated when they send new messages with the new name
              // For now, just update with the nickname if available
              if (nickname) {
                // Keep the seat number if it exists in the current name
                const match = message.senderName.match(/^(\d+)\.\s/);
                if (match) {
                  message.senderName = `${match[1]}. ${nickname}`;
                } else {
                  message.senderName = nickname;
                }
              }
            }
          }
        });
      }
    });
  },
  sendScreenshotMessage(state, { targetPlayerId, msgId, imageData, mode, ttl }) {
    if (!state.chats[targetPlayerId]) {
      Vue.set(state.chats, targetPlayerId, { messages: [], unreadCount: 0 });
    }
    state.chats[targetPlayerId].messages.push({
      senderId: state.playerId,
      senderName: "Storyteller",
      type: "screenshot",
      msgId,
      imageData,
      mode,
      ttl: ttl || null,
      viewedAt: null,
      revoked: false,
      timestamp: Date.now(),
    });
  },
  receiveScreenshot(state, { fromPlayerId, msgId, imageData, mode, ttl, timestamp, viewedAt }) {
    const chatPlayerId = fromPlayerId === "storyteller" ? "storyteller" : fromPlayerId;
    if (!state.chats[chatPlayerId]) {
      Vue.set(state.chats, chatPlayerId, { messages: [], unreadCount: 0 });
    }
    // Deduplicate by msgId — but update imageData/viewedAt if local copy lost them (e.g., after refresh)
    const existing = state.chats[chatPlayerId].messages.find(
      (m) => m.type === "screenshot" && m.msgId === msgId
    );
    if (existing) {
      if (imageData && !existing.imageData && !existing.revoked) {
        existing.imageData = imageData;
      }
      if (viewedAt && !existing.viewedAt) {
        existing.viewedAt = viewedAt;
      }
      return;
    }

    state.chats[chatPlayerId].messages.push({
      senderId: fromPlayerId,
      senderName: "Storyteller",
      type: "screenshot",
      msgId,
      imageData,
      mode,
      ttl: ttl || null,
      viewedAt: viewedAt || null,
      revoked: false,
      timestamp,
    });
    // Sort by timestamp to maintain order with text messages
    state.chats[chatPlayerId].messages.sort((a, b) => a.timestamp - b.timestamp);
    if (state.currentChatPlayerId !== chatPlayerId || state.isChatMinimized) {
      state.chats[chatPlayerId].unreadCount++;
    }
  },
  screenshotViewed(state, { chatPlayerId, msgId, viewedAt }) {
    if (!state.chats[chatPlayerId]) return;
    const msg = state.chats[chatPlayerId].messages.find(
      (m) => m.type === "screenshot" && m.msgId === msgId
    );
    if (msg && !msg.viewedAt) {
      msg.viewedAt = viewedAt || Date.now();
    }
  },
  screenshotExpired(state, { chatPlayerId, msgId }) {
    if (!state.chats[chatPlayerId]) return;
    const msg = state.chats[chatPlayerId].messages.find(
      (m) => m.type === "screenshot" && m.msgId === msgId
    );
    if (msg) {
      msg.imageData = null;
      msg.revoked = true;
    }
    // Close viewer if this screenshot is currently open
    if (state.screenshotViewer && state.screenshotViewer.msgId === msgId) {
      state.screenshotViewer = null;
    }
  },
  revokeScreenshot(state, { targetPlayerId, msgId }) {
    // ST-side: mark as revoked in local state
    if (!state.chats[targetPlayerId]) return;
    const msg = state.chats[targetPlayerId].messages.find(
      (m) => m.type === "screenshot" && m.msgId === msgId
    );
    if (msg) {
      msg.imageData = null;
      msg.revoked = true;
    }
  },
  handleScreenshotRevoked(state, { msgId }) {
    // Player-side: find and expire the screenshot
    for (let chatPlayerId in state.chats) {
      const msg = state.chats[chatPlayerId].messages.find(
        (m) => m.type === "screenshot" && m.msgId === msgId
      );
      if (msg) {
        msg.imageData = null;
        msg.revoked = true;
        break;
      }
    }
    if (state.screenshotViewer && state.screenshotViewer.msgId === msgId) {
      state.screenshotViewer = null;
    }
  },
  openScreenshotViewer(state, { msgId, imageData, mode, ttl, viewedAt }) {
    state.screenshotViewer = { msgId, imageData, mode, ttl, viewedAt };
  },
  closeScreenshotViewer(state) {
    state.screenshotViewer = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
